import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "@shared/utils/internationalization";
import Tooltip from "@ui-kit/overlays/Tooltip/Tooltip.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import Button from "@ui-kit/form/Button/Button.web";
import IconButton from "@ui-kit/form/IconButton/IconButton.web";
import Icon from "@ui-kit/icons/Icon.web";
// eslint-disable-next-line import/no-cycle -- Intentional: FormField renders RepeaterField for repeater type
import FormField from "../FormField/FormField.web";
import {
  RepeaterFormFieldConfig,
  RepeaterItemFieldConfig,
  SingleFieldConfig,
} from "../FormField/types";

interface RepeaterFieldProps {
  config: RepeaterFormFieldConfig;
}

/**
 * RepeaterField - A form field for managing arrays of items
 *
 * Renders rows of form fields using the existing FormField component,
 * allowing users to add, edit, and remove items.
 */
export default function RepeaterField({
  config,
}: RepeaterFieldProps): JSX.Element {
  const { t } = useTranslation("common");
  const { control } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: config.name,
  });

  const {
    itemFields,
    defaultItem = {},
    addButtonText = t("Add option"),
    showHeader = true,
    minItems = 0,
    maxItems,
  } = config;

  const handleAdd = () => {
    append(defaultItem);
  };

  const handleRemove = (index: number) => {
    if (fields.length > minItems) {
      const item = fields[index];
      config.onRemove?.(item as Record<string, unknown>, index);
      remove(index);
    }
  };

  const canAdd =
    (maxItems === undefined || fields.length < maxItems) && fields.length !== 0;
  const canRemove = fields.length > minItems;

  // Build a FormField config for a specific item field at a given index
  const buildFieldConfig = (
    itemField: RepeaterItemFieldConfig,
    index: number,
    rowData: Record<string, unknown>
  ): SingleFieldConfig => {
    const fieldName = `${config.name}.${index}.${itemField.name}`;

    // Calculate dynamic disabled and tooltip based on row data
    const isDisabled = itemField.disabled
      ? itemField.disabled(rowData)
      : itemField.field.disabled;
    const tooltip = itemField.dynamicTooltip
      ? itemField.dynamicTooltip(rowData)
      : itemField.field.tooltip;

    return {
      ...itemField.field,
      name: fieldName,
      // Hide the label in the field itself - we show it in the header
      hideLabel: true,
      disabled: isDisabled,
      tooltip,
    } as SingleFieldConfig;
  };

  return (
    <Box width="100%">
      {/* Header row with labels */}
      {showHeader && fields.length > 0 && (
        <Flex
          gap="sm"
          mb="xs"
          pb="xs"
          borderBottom="1px solid"
          borderColor="gray.200"
        >
          {itemFields.map((itemField) => (
            <Box key={itemField.name} flex={itemField.width || "1"}>
              <Flex alignItems="center" gap="2xs">
                <Text fontSize="xs" fontWeight="medium" color="gray.600">
                  {itemField.field.label}
                  {itemField.field.required && (
                    <Text as="span" color="red.500">
                      {" "}
                      *
                    </Text>
                  )}
                </Text>
                {itemField.tooltip && (
                  <Tooltip
                    label={itemField.tooltip}
                    placement="top"
                    hasArrow
                    shouldWrapChildren
                  >
                    <Box as="span" cursor="help" display="inline-flex">
                      <Icon.Info
                        width="element.sm"
                        height="element.sm"
                        color="gray.400"
                      />
                    </Box>
                  </Tooltip>
                )}
              </Flex>
            </Box>
          ))}
          {/* Spacer for delete button column */}
          <Box width="element.3xl" />
        </Flex>
      )}

      {/* Item rows */}
      <Flex direction="column" gap="xs">
        {fields.map((item, index) => {
          const rowData = item as Record<string, unknown>;
          return (
            <Flex
              key={item.id}
              gap="sm"
              alignItems="flex-start"
              p="xs"
              bg="gray.50"
              borderRadius="sm"
              _hover={{ bg: "gray.100" }}
            >
              {itemFields.map((itemField) => (
                <Box key={itemField.name} flex={itemField.width || "1"}>
                  <FormField
                    config={buildFieldConfig(itemField, index, rowData)}
                  />
                </Box>
              ))}

              {/* Delete button */}
              <Box width="element.3xl" pt="2xs">
                <IconButton
                  aria-label={t("Remove item")}
                  iconName="Trash"
                  iconColor="red.500"
                  iconSize="20px"
                  size="sm"
                  variant="ghost"
                  colorPalette="red"
                  onClick={() => handleRemove(index)}
                  isDisabled={!canRemove}
                />
              </Box>
            </Flex>
          );
        })}
      </Flex>

      {/* Add button */}
      {canAdd && (
        <Button
          leftIcon={<Icon.Plus color="inherit" />}
          variant="outline"
          size="sm"
          mt="sm"
          onClick={handleAdd}
          data-testid="add-option-button"
        >
          {addButtonText}
        </Button>
      )}

      {/* Empty state */}
      {fields.length === 0 && (
        <Box
          p="xl"
          textAlign="center"
          bg="gray.50"
          borderRadius="sm"
          border="1px dashed"
          borderColor="gray.300"
        >
          <Text color="gray.500" mb="sm">
            {t("No options added yet")}
          </Text>
          <Button
            leftIcon={<Icon.Plus color="inherit" />}
            variant="solid"
            colorPalette="primary"
            size="sm"
            onClick={handleAdd}
            data-testid="add-option-button"
          >
            {addButtonText}
          </Button>
        </Box>
      )}
    </Box>
  );
}
